import { Component, OnInit, ViewChild } from '@angular/core';
import { BenefitsMenuService } from '@benefits/services';
import { BenefitTable } from '@payroll/services';
import { AuthService, NotifyService } from '@app/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';
import { CompanyBenefit } from '@models/payroll/company/benefit.model';
import { ConfirmationDialogComponent } from '@app/components';
import { BenefitInvoicesTable } from '@benefits/services/tables/benefit-invoices-table/benefit-invoices.table';
import { BenefitCategory } from '@models/benefits/benefit-category.model';
import { BenefitType } from '@models/payroll/benefit-type.model';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';

@Component({
    templateUrl: './settings-index.template.html',
    styleUrls: ['./settings-index.style.scss'],
})
export class SettingsIndexView implements OnInit {
    @ViewChild('deleteConfirmationDialog') deleteConfirmationDialog!: ConfirmationDialogComponent;
    table?: BenefitTable;
    benefitsInvoiceTable?: BenefitInvoicesTable;
    isLoading = true;
    isNotAccessible = false;
    contactUsButtonDisabled = false;

    constructor(
        public readonly benefitsMenuService: BenefitsMenuService,
        public readonly auth: AuthService,
        public readonly route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private http: HttpClient
    ) {}

    async ngOnInit(): Promise<void> {
        this.isNotAccessible = Boolean(
            !this.auth.companyHasModule(Platform.modules.payroll) ||
                !(this.auth.company?.prTenantId !== null) ||
                !this.auth.company.isBenefitsPayrollIntegrationEnabled
        );

        if (this.isNotAccessible) {
            this.isLoading = false;
            return;
        }

        const benefitTypeIdsToFilter = await this.getBenefitTypeIdsForFilter();

        this.table = new BenefitTable(this.auth, benefitTypeIdsToFilter);
        this.benefitsInvoiceTable = new BenefitInvoicesTable(this.auth);

        this.isLoading = false;
    }

    async menuAction(event: MenuColumnEvent<CompanyBenefit>): Promise<void> {
        const { menuItem, data: benefit } = event;

        switch (menuItem.event) {
            case 'delete':
                if (!(await this.deleteConfirmationDialog.open())) {
                    return;
                }

                this.deleteBenefit(benefit);

                break;
            case 'edit':
                this.router.navigate(['/payroll', 'benefits', benefit?.id, 'edit'], { relativeTo: this.route });
                break;
        }
    }

    async contactClicked(): Promise<void> {
        try {
            await this.http
                .post(`${environment.api}/v2/benefitManagement/requestAccessToBenefitsPayroll`, null)
                .toPromise();
            this.notify.success('benefits.benefitsPayroll.requestWasReceived');
            this.contactUsButtonDisabled = true;
        } catch (err) {
            this.notify.error(err);
        }
    }

    private async deleteBenefit(benefit?: CompanyBenefit): Promise<void> {
        if (!benefit) {
            return;
        }

        try {
            this.isLoading = true;

            await benefit.param('company', this.auth.company.id).delete();

            this.notify.success('success');
            this.table?.reload();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private async getBenefitTypeIdsForFilter(): Promise<number[]> {
        const [[benefitCategories], [payrollBenefitTypes]] = await Promise.all([
            BenefitCategory.all(),
            BenefitType.get(),
        ]);

        const benefitTypeIdsToFilter: number[] = [];

        benefitCategories.forEach(function (benefitCategory) {
            if (!benefitCategory.payrollBenefitTypeName) {
                return;
            }

            const mappedBenefit = payrollBenefitTypes.find(
                (payrollBenefitType) => payrollBenefitType.name === benefitCategory.payrollBenefitTypeName
            );

            if (mappedBenefit && benefitTypeIdsToFilter.indexOf(mappedBenefit.id) === -1) {
                benefitTypeIdsToFilter.push(mappedBenefit.id);
            }
        });

        return benefitTypeIdsToFilter;
    }
}
